import React, { useState } from 'react';
import './styles/App.scss';
import Navbar from './components/Navbar';
import { Route, Routes, useLocation } from "react-router-dom";
import usePageTracking from './hooks/usePageTracking';
import scrollToTop from './utils/scrollToTop';
import FloatingContactModal from './components/FloatingContactModal';
import { AnimatePresence, motion } from 'framer-motion';
import Home from './components/Home';
import About from './components/About';
import Buyers from './components/Buyers';
import Sellers from './components/Sellers';
import Contact from './components/Contact';
import Testimonials from './components/Testimonials';
import Neighborhoods from './components/Neighborhoods';
import NeighborhoodDetail from './components/NeighborhoodDetail';
import MarketUpdates from './components/MarketUpdates';
import Properties from './components/Properties';
import Blog from './components/Blog';
import Insights from './components/Insights';
import InsightDetail from './components/InsightDetail';
import Communities from './components/Communities';
import Footer from './components/Footer';
import ScrollProgress from './components/ScrollProgress';
import PrivacyPolicy from './components/PrivacyPolicy';
import Subscribe from './components/Subscribe';
import HomeValuation from './components/HomeValuation';
import MortgageCalculator from './components/MortgageCalculator';
import EventRegistration from './components/EventRegistration';
import WestwoodHighlands from './components/WestwoodHighlands';
import NotFound from './components/NotFound';
import { Helmet } from 'react-helmet-async';
// Import only the Semantic UI components actually used — the full semantic.min.css is 550 KB
// of render-blocking CSS, versus ~226 KB for these. The set must cover every semantic-ui-react
// component in the app (Grid, Image, Button, Modal, Icon); omitting image.min.css previously
// left <Image> unconstrained and blew the About portrait out of its container.
// site.min.css is deliberately excluded: it carries an @import of Google Fonts (Lato), a
// render-blocking request to a third-party origin that cost ~7s of LCP on a throttled
// connection. The app declares its own typography, so nothing here needs it.
// dimmer + transition back Modal.
import 'semantic-ui-css/components/reset.min.css';
import 'semantic-ui-css/components/grid.min.css';
import 'semantic-ui-css/components/image.min.css';
import 'semantic-ui-css/components/button.min.css';
import 'semantic-ui-css/components/icon.min.css';
import 'semantic-ui-css/components/modal.min.css';
import 'semantic-ui-css/components/dimmer.min.css';
import 'semantic-ui-css/components/transition.min.css';

function App() {
  const location = useLocation();
  usePageTracking();

  const [contactOpen, setContactOpen] = useState(false);

  const hideLayoutRoutes = ['/subscribe', '/680-los-palmos']; // Add more routes if needed
  // Normalize a trailing slash so e.g. "/680-los-palmos/" still hides the chrome.
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const hideLayout = hideLayoutRoutes.includes(normalizedPath);

  return (
    <div className="app neue">
      <Helmet>
        <link rel='canonical' href={`https://www.lilylaisf.com${location.pathname}`} />
      </Helmet>
      <ScrollProgress />
      {!hideLayout && <Navbar />}
      <AnimatePresence mode="wait" onExitComplete={scrollToTop}>
        <motion.div
          key={location.key}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
        >
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="about" element={<About />} />
            <Route path="contact" element={<Contact />} />
            <Route path="testimonials" element={<Testimonials />} />
            <Route path="buyers" element={<Buyers />} />
            <Route path="sellers" element={<Sellers />} />
            <Route path="market-updates" element={<MarketUpdates />} />
            <Route path="properties" element={<Properties />} />
            <Route path="blog" element={<Blog />} />
            <Route path="insights" element={<Insights />} />
            <Route path="insights/:slug" element={<InsightDetail />} />
            <Route path="neighborhoods" element={<Neighborhoods />} />
            <Route path="neighborhoods/:slug" element={<NeighborhoodDetail />} />
            <Route path="communities" element={<Communities />} />
            <Route path="privacypolicy" element={<PrivacyPolicy />} />
            <Route path="subscribe" element={<Subscribe />} />
            <Route path="home-valuation" element={<HomeValuation />} />
            <Route path="mortgage-calculator" element={<MortgageCalculator />} />
            <Route path="events/fall-social" element={<EventRegistration />} />
            <Route path="680-los-palmos" element={<WestwoodHighlands />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </motion.div>
      </AnimatePresence>
      {!hideLayout && <Footer />}
      {location.pathname !== '/contact' && !hideLayout && (
        <button
          type="button"
          className="floating-contact-btn"
          onClick={() => setContactOpen(true)}
        >
          Contact
        </button>
      )}
      <FloatingContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}

export default App;
